/**
 * Canonical Element Model (CEM) mock payloads for the Web Plugin.
 *
 * These fixtures represent what the native BIM plugins (Revit, ArchiCAD,
 * Vectorworks, etc.) POST to the Web API after the Story 179 canonical-projection
 * layer normalises host-specific parameters. Each mock is selected via
 * `?elementType=<slug>` in the plugin URL (see useMessageHandler.ts) and is
 * used to exercise the Web Plugin UI, validation endpoint, streaming SSE,
 * citation resolver and deterministic rules without launching a native BIM host.
 *
 * Payload shape emitted (the JSON object encoded once in `Content`):
 * ```
 * {
 *   "id": "<stable-id>",
 *   "type": "CanonicalStair",
 *   "properties": { "riserHeightMm": 190, "goingMm": 250, ... }
 * }
 * ```
 *
 * Latitude / Longitude are decimal degrees (WGS84), matching the CEM
 * coordinate convention and the README `?elementType=` catalogue.
 *
 * The `canonical` object matches the minimal Canonical Element Model surface
 * declared in `BIMComply.RuleEngine/Canonical/ICanonicalElement.cs` (Id, Type,
 * flat Properties bag) and the per-target property names used by the BC-CEL
 * rule fixtures (Rules/IE/TGD-K/*.json — riserHeightMm, goingMm, useClass, ...).
 * The wrapper context fields (BuildingType, PlaceName, Latitude, Longitude,
 * NumberOfLevels) are preserved so the AI validator prompt continues to work.
 *
 * Numeric values follow Irish TGD conventions: lengths in millimetres,
 * areas in square metres, U-values in W/m²K, Part K stair values sized so the
 * default fixtures are compliant against the deterministic engine.
 */

import {
    getFixture,
    hasRegisteredElement,
    type CanonicalPackagePayload,
    type FixtureVariant,
} from '../fixtures/canonical';

export type CanonicalType =
    | 'CanonicalStair'
    | 'CanonicalRailing'
    | 'CanonicalDoor'
    | 'CanonicalWindow'
    | 'CanonicalRoom'
    | 'CanonicalFloor'
    | 'CanonicalWall'
    | 'CanonicalRoof'
    | 'CanonicalOpening'
    | 'CanonicalStorey'
    | 'CanonicalBuilding'
    | 'CanonicalRamp'
    | 'CanonicalColumn'
    | 'CanonicalBeam'
    | 'CanonicalCurtainWall'
    | 'CanonicalGrabBar'
    | 'CanonicalDwellingUnit'
    | 'CanonicalSanitaryTerminal';

export type ElementSlug =
    | 'stairs'
    | 'railing'
    | 'door'
    | 'window'
    | 'room'
    | 'floor'
    | 'wall'
    | 'roof'
    | 'opening'
    | 'storey'
    | 'building'
    | 'ramp'
    | 'column'
    | 'beam'
    | 'curtainwall'
    | 'grabbar'
    | 'group'
    | 'sanitaryterminal';

export interface CanonicalElementPayload {
    id: string;
    type: CanonicalType;
    properties: Record<string, unknown>;
}

export interface CanonicalMockPayload {
    BuildingType: string;
    PlaceName: string;
    Latitude: number;
    Longitude: number;
    NumberOfLevels: number;
    canonical: CanonicalElementPayload;
}

export interface CanonicalMockResult {
    defaultElementType: string;
    content: string;
    payload: CanonicalElementPayload;
}

// Dublin, Ireland — decimal degrees (WGS84).
const DUBLIN_LAT = 53.3498;
const DUBLIN_LON = -6.2603;

function withContext(
    canonical: CanonicalElementPayload,
    overrides: Partial<Omit<CanonicalMockPayload, 'canonical'>> = {},
    placeName?: string,
): CanonicalMockPayload {
    return {
        BuildingType: overrides.BuildingType ?? 'SingleFamily',
        PlaceName: overrides.PlaceName ?? (placeName || 'Dublin, Ireland'),
        Latitude: overrides.Latitude ?? DUBLIN_LAT,
        Longitude: overrides.Longitude ?? DUBLIN_LON,
        NumberOfLevels: overrides.NumberOfLevels ?? 3,
        canonical,
    };
}

type MockBuilder = (placeName: string) => { defaultElementType: string; payload: CanonicalMockPayload };

const stairs: MockBuilder = (placeName) => ({
    defaultElementType: 'Stair',
    payload: withContext(
        {
            id: 'stair-1',
            type: 'CanonicalStair',
            properties: {
                useClass: 'Private',
                riserHeightMm: { value: 190, unit: 'mm' },
                goingMm: { value: 250, unit: 'mm' },
                pitchDegrees: 37,
                headroomMm: { value: 2100, unit: 'mm' },
                effectiveWidthMm: { value: 900, unit: 'mm' },
                handrailHeightMm: { value: 950, unit: 'mm' },
                riserCount: 14,
                twoRPlusG: { value: 630, unit: 'mm' },
                flights: [
                    {
                        riserCount: 14,
                        risersMm: Array(14).fill({ value: 190, unit: 'mm' }),
                        goingsMm: Array(13).fill({ value: 250, unit: 'mm' }),
                    },
                ],
                landings: [
                    {
                        lengthMm: { value: 1000, unit: 'mm' },
                        effectiveWidthMm: { value: 900, unit: 'mm' },
                    },
                ],
            },
        },
        { BuildingType: 'SingleFamily', NumberOfLevels: 3 },
        placeName,
    ),
});

const railing: MockBuilder = (placeName) => ({
    defaultElementType: 'Railing',
    payload: withContext(
        {
            id: 'railing-1',
            type: 'CanonicalRailing',
            properties: {
                heightMm: 1100,
                lengthMm: 7000,
                gapMm: 99,
                isProtective: true,
                hostElementId: 'stair-1',
                material: 'Steel + Glass Infill',
                useClass: 'Private',
            },
        },
        { BuildingType: 'SingleFamily', NumberOfLevels: 4 },
        placeName,
    ),
});

const door: MockBuilder = (placeName) => ({
    defaultElementType: 'Door',
    payload: withContext(
        {
            id: 'door-1',
            type: 'CanonicalDoor',
            properties: {
                widthMm: 900,
                heightMm: 2040,
                fireRatingMinutes: 30,
                uValueWm2K: 1.4,
                isAccessible: true,
                isExternal: false,
                hostWallId: 'wall-1',
                toRoomId: 'room-1',
                fromRoomId: 'room-2',
                openingDirection: 'Inward',
            },
        },
        { BuildingType: 'SingleFamily', NumberOfLevels: 4 },
        placeName,
    ),
});

const grabbar: MockBuilder = (placeName) => ({
    defaultElementType: 'Grab Bar',
    payload: withContext(
        {
            id: 'grabbar-1',
            type: 'CanonicalGrabBar',
            properties: {
                lengthMm: 600,
                mountingHeightMm: 800,
                diameterMm: 32,
                loadRatingKg: 150,
                isAccessible: true,
                hostRoomId: 'room-wc-1',
                orientation: 'Horizontal',
            },
        },
        { BuildingType: 'Office', NumberOfLevels: 18 },
        placeName,
    ),
});

const group: MockBuilder = (placeName) => ({
    defaultElementType: 'Dwelling Unit',
    payload: withContext(
        {
            id: 'dwelling-203',
            type: 'CanonicalDwellingUnit',
            properties: {
                nameEn: 'Studio Unit',
                unitType: 'Studio',
                floorAreaM2: 55,
                bedroomCount: 0,
                occupancy: 2,
                storeyRef: 'storey-2',
                roomIds: ['room-studio-1', 'room-wc-1'],
            },
        },
        { BuildingType: 'Apartment', NumberOfLevels: 6 },
        placeName,
    ),
});

const windowMock: MockBuilder = (placeName) => ({
    defaultElementType: 'Window',
    payload: withContext(
        {
            id: 'window-1',
            type: 'CanonicalWindow',
            properties: {
                widthMm: 1200,
                heightMm: 1500,
                sillMm: 900,
                uValueWm2K: 1.2,
                gValue: 0.5,
                openable: true,
                isExternal: true,
                hostWallId: 'wall-ext-1',
            },
        },
        { BuildingType: 'SingleFamily', NumberOfLevels: 3 },
        placeName,
    ),
});

const room: MockBuilder = (placeName) => ({
    defaultElementType: 'Room',
    payload: withContext(
        {
            id: 'room-1',
            type: 'CanonicalRoom',
            properties: {
                nameEn: 'Living Room',
                number: '101',
                areaM2: 18.5,
                perimeterMm: 17200,
                ceilingHeightMm: 2400,
                occupancy: 'Residential',
                storeyRef: 'storey-1',
                boundaryWallIds: ['wall-1', 'wall-2', 'wall-3', 'wall-4'],
            },
        },
        { BuildingType: 'SingleFamily', NumberOfLevels: 2 },
        placeName,
    ),
});

const floor: MockBuilder = (placeName) => ({
    defaultElementType: 'Floor',
    payload: withContext(
        {
            id: 'floor-1',
            type: 'CanonicalFloor',
            properties: {
                areaM2: 92.5,
                thicknessMm: 250,
                uValueWm2K: 0.18,
                storeyRef: 'storey-1',
                isGroundFloor: true,
                materialLayers: [
                    { name: 'Screed', thicknessMm: 65 },
                    { name: 'Insulation (PIR)', thicknessMm: 120 },
                    { name: 'Concrete Slab', thicknessMm: 150 },
                    { name: 'DPM', thicknessMm: 1 },
                ],
            },
        },
        { BuildingType: 'SingleFamily', NumberOfLevels: 2 },
        placeName,
    ),
});

const wall: MockBuilder = (placeName) => ({
    defaultElementType: 'Wall',
    payload: withContext(
        {
            id: 'wall-1',
            type: 'CanonicalWall',
            properties: {
                lengthMm: 5200,
                heightMm: 2700,
                thicknessMm: 300,
                uValueWm2K: 0.18,
                fireRatingMinutes: 60,
                isExternal: true,
                isLoadBearing: true,
                storeyRef: 'storey-1',
                materialLayers: [
                    { name: 'Plaster', thicknessMm: 13 },
                    { name: 'Blockwork Inner Leaf', thicknessMm: 100 },
                    { name: 'Insulation (Mineral Wool)', thicknessMm: 150 },
                    { name: 'Cavity', thicknessMm: 50 },
                    { name: 'Brick Outer Leaf', thicknessMm: 100 },
                ],
            },
        },
        { BuildingType: 'SingleFamily', NumberOfLevels: 2 },
        placeName,
    ),
});

const roof: MockBuilder = (placeName) => ({
    defaultElementType: 'Roof',
    payload: withContext(
        {
            id: 'roof-1',
            type: 'CanonicalRoof',
            properties: {
                areaM2: 110,
                pitchDegrees: 35,
                uValueWm2K: 0.16,
                projectedAreaM2: 92,
                materialLayers: [
                    { name: 'Slate Tiles', thicknessMm: 8 },
                    { name: 'Battens', thicknessMm: 25 },
                    { name: 'Breather Membrane', thicknessMm: 1 },
                    { name: 'Rafters w/ Insulation', thicknessMm: 220 },
                    { name: 'Plasterboard', thicknessMm: 12.5 },
                ],
            },
        },
        { BuildingType: 'SingleFamily', NumberOfLevels: 2 },
        placeName,
    ),
});

const opening: MockBuilder = (placeName) => ({
    defaultElementType: 'Opening',
    payload: withContext(
        {
            id: 'opening-1',
            type: 'CanonicalOpening',
            properties: {
                widthMm: 1200,
                heightMm: 2100,
                sillMm: 0,
                headMm: 2100,
                hostWallId: 'wall-1',
                purpose: 'Door',
            },
        },
        { BuildingType: 'SingleFamily', NumberOfLevels: 2 },
        placeName,
    ),
});

const storey: MockBuilder = (placeName) => ({
    defaultElementType: 'Storey',
    payload: withContext(
        {
            id: 'storey-1',
            type: 'CanonicalStorey',
            properties: {
                nameEn: 'Ground Floor',
                elevationMm: 0,
                heightMm: 2700,
                isBasement: false,
                buildingRef: 'building-1',
                grossFloorAreaM2: 92.5,
            },
        },
        { BuildingType: 'SingleFamily', NumberOfLevels: 2 },
        placeName,
    ),
});

const building: MockBuilder = (placeName) => ({
    defaultElementType: 'Building',
    payload: withContext(
        {
            id: 'building-1',
            type: 'CanonicalBuilding',
            properties: {
                nameEn: 'Sample House',
                buildingType: 'SingleFamily',
                useClass: 'Dwelling',
                occupancyType: 'Residential',
                storeyCount: 2,
                grossFloorAreaM2: 185,
                addressLine1: '1 Sample Street',
                localityEn: 'Dublin',
                country: 'Ireland',
                jurisdiction: 'IE',
            },
        },
        { BuildingType: 'SingleFamily', NumberOfLevels: 2 },
        placeName,
    ),
});

const ramp: MockBuilder = (placeName) => ({
    defaultElementType: 'Ramp',
    payload: withContext(
        {
            id: 'ramp-1',
            type: 'CanonicalRamp',
            properties: {
                gradientPercent: 6.25,
                lengthMm: 6000,
                widthMm: 1200,
                riseM: 0.375,
                landingLengthMm: 1500,
                useClass: 'Common',
                handrailHeightMm: 950,
                surfaceFinish: 'Slip-Resistant',
            },
        },
        { BuildingType: 'Office', NumberOfLevels: 3 },
        placeName,
    ),
});

const column: MockBuilder = (placeName) => ({
    defaultElementType: 'Column',
    payload: withContext(
        {
            id: 'column-1',
            type: 'CanonicalColumn',
            properties: {
                heightMm: 2700,
                widthMm: 300,
                depthMm: 300,
                material: 'Reinforced Concrete C30/37',
                isLoadBearing: true,
                fireRatingMinutes: 90,
                storeyRef: 'storey-1',
            },
        },
        { BuildingType: 'Office', NumberOfLevels: 5 },
        placeName,
    ),
});

const beam: MockBuilder = (placeName) => ({
    defaultElementType: 'Beam',
    payload: withContext(
        {
            id: 'beam-1',
            type: 'CanonicalBeam',
            properties: {
                lengthMm: 6000,
                spanMm: 5800,
                widthMm: 250,
                depthMm: 450,
                material: 'Steel S275 - UB 457x152x60',
                isLoadBearing: true,
                fireRatingMinutes: 60,
                storeyRef: 'storey-1',
            },
        },
        { BuildingType: 'Office', NumberOfLevels: 5 },
        placeName,
    ),
});

const curtainwall: MockBuilder = (placeName) => ({
    defaultElementType: 'Curtain Wall',
    payload: withContext(
        {
            id: 'curtainwall-1',
            type: 'CanonicalCurtainWall',
            properties: {
                heightMm: 3000,
                widthMm: 8400,
                uValueWm2K: 1.4,
                gValue: 0.35,
                panelCountHorizontal: 7,
                panelCountVertical: 2,
                glazingRatioPercent: 85,
                mullionSpacingMm: 1200,
                isExternal: true,
            },
        },
        { BuildingType: 'Office', NumberOfLevels: 10 },
        placeName,
    ),
});

const sanitaryterminal: MockBuilder = (placeName) => ({
    defaultElementType: 'Sanitary Terminal',
    payload: withContext(
        {
            id: 'wc-1',
            type: 'CanonicalSanitaryTerminal',
            properties: {
                fixtureType: 'WaterCloset',
                waterConsumptionLitresPerUse: 4.5,
                mountingHeightMm: 480,
                isAccessible: true,
                hostRoomId: 'room-wc-1',
                clearanceRadiusMm: 1500,
            },
        },
        { BuildingType: 'Office', NumberOfLevels: 3 },
        placeName,
    ),
});

export const CANONICAL_MOCK_BUILDERS: Readonly<Record<ElementSlug, MockBuilder>> = Object.freeze({
    railing,
    door,
    stairs,
    grabbar,
    group,
    window: windowMock,
    room,
    floor,
    wall,
    roof,
    opening,
    storey,
    building,
    ramp,
    column,
    beam,
    curtainwall,
    sanitaryterminal,
});

/** All supported `?elementType=` slugs, in catalogue order. Frozen and typed
 *  as a tuple of `ElementSlug` so any accidental addition, removal or rename
 *  fails at compile time. */
export const SUPPORTED_ELEMENT_TYPES: readonly ElementSlug[] = Object.freeze(
    Object.keys(CANONICAL_MOCK_BUILDERS) as ElementSlug[],
);

const ELEMENT_TYPE_ALIASES: Readonly<Record<ElementSlug, readonly string[]>> = Object.freeze({
    railing: ['railing', 'railings', 'CanonicalRailing'],
    door: ['door', 'doors', 'CanonicalDoor'],
    stairs: ['stair', 'stairs', 'CanonicalStair'],
    grabbar: ['grabbar', 'grab bar', 'specialty equipment', 'CanonicalGrabBar'],
    group: ['group', 'dwelling unit', 'dwellingunit', 'CanonicalDwellingUnit'],
    window: ['window', 'windows', 'CanonicalWindow'],
    room: ['room', 'rooms', 'CanonicalRoom'],
    floor: ['floor', 'floors', 'CanonicalFloor'],
    wall: ['wall', 'walls', 'CanonicalWall'],
    roof: ['roof', 'roofs', 'CanonicalRoof'],
    opening: ['opening', 'openings', 'CanonicalOpening'],
    storey: ['storey', 'storeys', 'story', 'stories', 'CanonicalStorey'],
    building: ['building', 'buildings', 'CanonicalBuilding'],
    ramp: ['ramp', 'ramps', 'CanonicalRamp'],
    column: ['column', 'columns', 'CanonicalColumn'],
    beam: ['beam', 'beams', 'CanonicalBeam'],
    curtainwall: ['curtainwall', 'curtain wall', 'curtain walls', 'CanonicalCurtainWall'],
    sanitaryterminal: ['sanitaryterminal', 'sanitary terminal', 'sanitary terminals', 'CanonicalSanitaryTerminal'],
});

function normalizeElementType(value: string): string {
    return value.trim().toLowerCase().replace(/[\s_-]+/g, '');
}

const ELEMENT_TYPE_LOOKUP: Readonly<Record<string, ElementSlug>> = Object.freeze(
    Object.entries(ELEMENT_TYPE_ALIASES).reduce<Record<string, ElementSlug>>(
        (lookup, [slug, aliases]) => {
            aliases.forEach(alias => {
                lookup[normalizeElementType(alias)] = slug as ElementSlug;
            });
            return lookup;
        },
        {},
    ),
);

export function resolveElementSlug(elementType: string | null | undefined): ElementSlug {
    return tryResolveElementSlug(elementType) ?? 'window';
}

export function tryResolveElementSlug(
    elementType: string | null | undefined,
): ElementSlug | null {
    if (!elementType) {
        return null;
    }

    return ELEMENT_TYPE_LOOKUP[normalizeElementType(elementType)] ?? null;
}

/**
 * Build the canonical mock payload for a given `?elementType=` slug. Unknown
 * or missing slugs fall back to the `window` fixture, matching the previous
 * `useMessageHandler` behaviour.
 */
export function buildCanonicalMock(
    elementType: string | null | undefined,
    placeName: string,
): CanonicalMockResult {
    const builder = CANONICAL_MOCK_BUILDERS[resolveElementSlug(elementType)];
    const { defaultElementType, payload: legacyPayload } = builder(placeName);
    const payload = legacyPayload.canonical;
    return {
        defaultElementType,
        content: JSON.stringify(payload),
        payload,
    };
}

/**
 * Debug-only fixture accessor. Returns a `CanonicalMockResult` sourced from
 * the versioned canonical fixture registry (`src/fixtures/canonical`). Never
 * called from the production request-normalisation path — used exclusively by
 * `useMessageHandler` when the plugin URL carries an explicit `?variant=` or
 * `?schemaVersion=` debug parameter.
 */
export function buildCanonicalDebugFixture(
    elementType: string | null | undefined,
    variant: FixtureVariant,
    options: { schemaVersion?: string } = {},
): (Omit<CanonicalMockResult, 'payload'> & {
    payload: CanonicalPackagePayload;
    schemaVersion: string;
}) | null {
    const slug = tryResolveElementSlug(elementType);
    if (!slug) {
        return null;
    }
    if (!hasRegisteredElement(slug)) {
        return null;
    }
    const fixture = getFixture(slug, variant, {
        schemaVersion: options.schemaVersion,
    });
    if (!fixture) {
        return null;
    }
    return {
        defaultElementType: fixture.defaultElementType ?? slug,
        content: JSON.stringify(fixture.payload),
        payload: fixture.payload,
        schemaVersion: fixture.schemaVersion,
    };
}
